import {getAllSolutionFilters, getAmexSolution} from '@/store/state/helpers';
import {startOfDay} from '@mobi-components/global-date-time-picker';
import {
  Activity,
  CandidateTransportationItem,
  lang,
  TransportationSegment,
  UserActivityCategoryEnum,
} from '@mobi-components/global-solution';
import {computed} from '@vue/composition-api';
import {useFlightPrices, getFlightPrice} from './useFlightPrices';
// @ts-ignore
import AirlinePdf from '../../svg/airline-pdf.svg';
import {fixDate} from '@/store/state/fix-solution-dates';
import {isFlightsAvailable} from '@/store/state/utils';
import {useParams} from '@/router/router';

export function getDateParts(x: Date) {
  const localTime = x.toLocaleTimeString(lang(), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const startOfTheDay = startOfDay(x);
  const localDate = startOfTheDay.toLocaleDateString(lang(), {month: 'short', day: 'numeric'});
  return {
    date: localDate,
    time: localTime,
  };
}

export function useMainFlight() {
  const solution = computed(() => {
    return getAmexSolution();
  });

  const availableSolutionsFlightTags = computed(() => {
    return getAllSolutionFilters()
      .filter((x) => x.domain === UserActivityCategoryEnum.TRANSPORTATION && x.name)
      .map((x) => x.name);
  });

  const flights = computed(() => {
    // return (solution.value?.activities || []).filter((x) => x.category === UserActivityCategoryEnum.FLIGHT);
    const params = useParams();
    const context = params.value.contextName;
    return (
      solution.value?.activities
        .filter((x) => x.category === UserActivityCategoryEnum.FLIGHT)
        .filter((x) => {
          // @ts-ignore
          const segments = x.source.content?.transportation?.segments || [];
          // @ts-ignore
          const from: string = segments.map((y) => y.origin).join(', ');
          // @ts-ignore
          const to = segments.map((y) => y.destination).join(', ');

          const both = from.concat(to);

          return !context || x.source.content?.contexts.includes(context) || both.includes(context);
        }) || []
    );
  });
  const isGreatPick = computed(() => {
    return flights.value.some((x) => x.source?.content?.preferred);
  });

  const isFlightAvailable = computed<boolean>(() => {
    // @ts-ignore flights exist and no estimations
    // return !!(flights.value.length && !flights.value.some((x) => (x.prices || []).some((y) => y.estimated)));
    return isFlightsAvailable(flights.value);
  });

  const flightsImg = computed(() => {
    const isEstimated = !isFlightAvailable.value;
    if (isEstimated) {
      return [AirlinePdf, AirlinePdf].slice(0, 2);
    }

    return flights.value.map((x) => x.images?.medium);
  });
  function getRecord<T>(values: T[]): Record<number, T> {
    return values.reduce((h: Record<number, T>, x, i) => {
      h[i] = x;
      return h;
    }, {});
  }
  const perks = computed<string[]>(() => {
    const activeFilters = availableSolutionsFlightTags.value || [];
    const hash = activeFilters.reduce((r: Record<string, boolean>, x) => {
      r[x.toLowerCase()] = true;
      return r;
    }, {});

    return flights.value
      .map((flight) => {
        return (flight?.source?.content?.explanations || [])
          .concat(flight?.source?.content?.tags || [])
          .concat(flight?.source?.content?.types || [])
          .concat(flight?.types || [])
          .filter((x) => hash[x.toLowerCase()]);
      })
      .flat()
      .filter((x, i, self) => self.indexOf(x) === i)
      .slice(0, 3);
  });

  const price = computed<string>(() => {
    // @ts-ignore
    const {price: res} = useFlightPrices(flights.value, solution.value?.activity_groups);
    return res;
  });
  const getFlightDetails = (flight: Activity) => {
    return flight.name
      .split(' ')
      .filter((_, i) => i === 0)
      .join('');
  };

  const getFlightDirections = (flight: Activity) => {
    const content = flight.source?.content as CandidateTransportationItem;
    const origin = (content?.origin?.name || '').replace(/_/g, ', ');
    const destination = (content?.destination?.name || '').replace(/_/g, ', ');
    return origin && destination ? `${origin}-${destination}` : '';
  };

  const getDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const fixedMinutes = minutes.toString().length > 1 ? `${minutes.toString()}m` : `0${minutes.toString()}m`;
    const fixedHours = hours > 0 ? `${hours}h ` : '';

    return `${fixedHours}${fixedMinutes}`;
  };
  const getStopsDuration = (segments: TransportationSegment[]): string => {
    const stops = [];
    for (let i = 0; i < segments.length - 1; i++) {
      const first = segments[i];
      const second = segments[i + 1];
      const duration = getDuration(second.departure_timestamp - first.arrival_timestamp);
      stops.push(duration);
    }
    return stops.length ? `${stops.join(', ')}` : '';
  };
  const getStopsNames = (segments: TransportationSegment[]) => {
    return segments
      .map((x) => x.destination)
      .slice(0, -1)
      .join(', ');
  };
  const getStopsWithDuration = (segments: TransportationSegment[]) => {
    const names = getStopsNames(segments).split(', ');
    const durations = getStopsDuration(segments).split(', ');
    return names
      .map((x, i) => {
        return `${x} (${durations[i]})`;
      })
      .join(', ');
  };
  const getStopsNumber = (flight: Activity) => {
    if (!flight) {
      return '';
    }
    const content = flight.source?.content as CandidateTransportationItem;
    const segments = (content?.transportation?.segments?.length || 1) - 1;
    if (!segments) {
      return 'Non-stop';
    }
    const stop = segments > 1 ? 'Stops' : 'Stop';
    const stopNames = getStopsWithDuration(content?.transportation?.segments || []);
    if (segments > 0) {
      return `${segments} ${stop} ${stopNames ? 'in ' + stopNames : ''}`;
    }
    return '';
  };
  const getAirlineName = (flight: Activity) => {
    if (!flight) {
      return '';
    }

    return [...new Set(flight?.types?.slice(0, 2))].join(', ') || '';
  };
  const airlines = computed<Record<number, string>>(() => {
    return flights.value
      .map(getAirlineName)
      .filter((x, i, s) => s.indexOf(x) === i)
      .join(', ');
  });
  const getFlightDates = (flight: Activity) => {
    if (!flight) {
      return '';
    }

    const content = flight.source?.content as CandidateTransportationItem;
    const segments = content?.transportation?.segments || [];

    // @ts-ignore
    const minDate = Math.min(...segments.map((x) => fixDate(x.departure_timestamp, x.origin_timezone)));
    // @ts-ignore
    const maxDate = Math.max(...segments.map((x) => fixDate(x.arrival_timestamp, x.destination_timezone)));

    const departureTime =
      minDate === Infinity ? new Date(flight.scheduled_start_time * 1000) : new Date(minDate * 1000);
    const arrivalTime = maxDate === -Infinity ? new Date(flight.scheduled_end_time * 1000) : new Date(maxDate * 1000);
    const {date: departureLocalDate, time: departureLocalTime} = getDateParts(departureTime);
    const {date: arrivalLocalDate, time: arrivalLocalTime} = getDateParts(arrivalTime);
    const departureDate = startOfDay(departureTime);
    const arrivalDate = startOfDay(arrivalTime);
    const dateDiff = arrivalDate.valueOf() - departureDate.valueOf();
    const diff = dateDiff > 0 ? `+${Math.floor(dateDiff / 24 / 3600 / 1000)}` : '';

    return {
      departure: `${departureLocalDate}, ${departureLocalTime}`,
      arrival: `${arrivalLocalDate === departureLocalDate ? '' : arrivalLocalDate + ', '}${arrivalLocalTime}`,
      arrivalLocalDate: arrivalLocalDate,
      arrivalLocalTime: arrivalLocalTime,
      departureLocalTime,
      departureLocalDate,
      arrivalTime,
      departureTime,
      duration: getDuration(flight.duration),
      images: flight?.images.medium,
      diff,
      airlines: getAirlineName(flight),
      details: getFlightDetails(flight),
      directions: getFlightDirections(flight),
      numberOfStops: getStopsNumber(flight),
      price: getFlightPrice(flight, 2),
      segments,
    };
  };
  const dates = computed(() => {
    return getRecord(flights.value.map(getFlightDates));
  });

  const firstArrivalTime = computed(() => {
    if (!dates.value || !dates.value[0]) {
      return '';
    }
    return dates.value[0].arrival;
  });
  const firstArrivalDate = computed(() => {
    if (!dates.value || !dates.value[0]) {
      return '';
    }
    return dates.value[0].arrivalLocalDate;
  });
  const firstArrivalTimeOnly = computed(() => {
    if (!dates.value || !dates.value[0]) {
      return '';
    }
    return dates.value[0].arrivalLocalTime;
  });

  const lastDepartureTime = computed(() => {
    if (!dates.value || !dates.value[1]) {
      return '';
    }
    return dates.value[1].departure;
  });
  return {
    perks,
    price,
    airlines,
    dates,
    flightsImg,
    isGreatPick,
    firstArrivalTime,
    lastDepartureTime,
    flights,
    isFlightAvailable,
    firstArrivalDate,
    firstArrivalTimeOnly,
    getStopsNumber,
  };
}
